import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { config } from '../../../config';
import { Edit3, Trash2, FileText } from 'lucide-react';
import UpdateNote from './UpdateNote';

const apiUrl = config.apiUrl;

const ViewNotes = () => {
  const [notes, setNotes] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [formData, setFormData] = useState({
    subject_id: '',
    title: '',
    content: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [notesResponse, subjectsResponse] = await Promise.all([
          axios.get(`${apiUrl}/notes`),
          axios.get(`${apiUrl}/subjects`)
        ]);
        
        // Handle both response formats
        setNotes(notesResponse.data.data || notesResponse.data);
        setSubjects(subjectsResponse.data.data || subjectsResponse.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('ডেটা লোড করতে ব্যর্থ হয়েছে।');
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('আপনি কি এই নোটটি মুছতে চান?')) {
      try {
        await axios.delete(`${apiUrl}/notes/${id}`);
        setNotes(prev => prev.filter(note => note.id !== id));
      } catch (err) {
        console.error('Error deleting note:', err);
        setError('নোট মুছতে ব্যর্থ হয়েছে।');
      }
    }
  };

  const handleEditClick = (note) => {
    setEditingNote(note.id);
    setFormData({
      id: note.id,
      subject_id: note.subject_id,
      title: note.title,
      content: note.content
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-center mb-6">
            <FileText className="h-8 w-8 text-blue-600 mr-2" />
            <h2 className="text-2xl font-bold text-gray-800">নোট ব্যবস্থাপনা</h2>
          </div>
          
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {notes.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">কোনো নোট পাওয়া যায়নি</p>
              <p className="text-gray-500">আপনার প্রথম নোট তৈরি করুন!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      শিরোনাম
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      বিষয়
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      বিষয়বস্তু
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      কর্ম
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {notes.map(note => (
                    <tr key={note.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{note.title}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                          {note.subject?.name || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div 
                          className="max-w-md line-clamp-2 text-gray-600"
                          dangerouslySetInnerHTML={{ 
                            __html: note.content?.substring(0, 100) + (note.content?.length > 100 ? '...' : '') 
                          }}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap space-x-2">
                        <button
                          onClick={() => handleEditClick(note)}
                          className="text-blue-600 hover:text-blue-900 px-3 py-2 rounded-lg hover:bg-blue-50 transition-colors flex items-center"
                          title="সম্পাদনা"
                        >
                          <Edit3 className="h-4 w-4 mr-1" />
                          সম্পাদনা
                        </button>
                        <button
                          onClick={() => handleDelete(note.id)}
                          className="text-red-600 hover:text-red-900 px-3 py-2 rounded-lg hover:bg-red-50 transition-colors flex items-center"
                          title="মুছুন"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          মুছুন
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {editingNote && (
            <UpdateNote
              formData={formData}
              setFormData={setFormData}
              subjects={subjects}
              onUpdate={(updatedNote) => {
                setNotes(prev =>
                  prev.map(note =>
                    note.id === editingNote ? { ...note, ...updatedNote } : note
                  )
                );
                setEditingNote(null);
                setFormData({
                  subject_id: '',
                  title: '',
                  content: ''
                });
              }}
              onCancel={() => {
                setEditingNote(null);
                setFormData({
                  subject_id: '',
                  title: '',
                  content: ''
                });
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewNotes;